/**
 * Per-address delivery record (#1911).
 *
 * A bulk send partially fails as its NORMAL case: one bounced mailbox out of
 * nine hundred is a Tuesday, not an incident, and rolling the notice back for
 * it would un-tell the other eight hundred and ninety-nine. So failure is
 * displayed per address, with the provider's own reason and a retry scoped to
 * that address — the retry passes exactly that account id, so pressing it does
 * not re-mail everyone who already received the notice.
 */
import type { EmailNoticeRecipient } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  Table,
  TableEmpty,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import { recipientStatusLabel, recipientStatusTone } from './noticeStatus.ts';

interface Props {
  readonly recipients: readonly EmailNoticeRecipient[];
  readonly retrying: boolean;
  readonly onRetry: (accountId: string) => void;
  readonly onRetryAllFailed: (accountIds: readonly string[]) => void;
}

export function NoticeDeliveryTable({
  recipients,
  retrying,
  onRetry,
  onRetryAllFailed,
}: Props): JSX.Element {
  const t = useT();
  const failed = recipients.filter((row) => row.status === 'failed');
  const sent = recipients.filter((row) => row.status === 'sent');

  return (
    <div className="flex flex-col gap-2" data-testid="email-notice-delivery">
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone="ok" testId="email-notice-delivery-sent">
          {t('email_notices.delivery.sent_count', { count: String(sent.length) })}
        </Chip>
        <Chip tone={failed.length === 0 ? 'neutral' : 'bad'} testId="email-notice-delivery-failed">
          {t('email_notices.delivery.failed_count', { count: String(failed.length) })}
        </Chip>
        {failed.length > 0 ? (
          <Button
            size="sm"
            variant="secondary"
            loading={retrying}
            onClick={() => onRetryAllFailed(failed.map((row) => row.account_id))}
            data-testid="email-notice-retry-all-failed"
          >
            {t('email_notices.delivery.retry_all', { count: String(failed.length) })}
          </Button>
        ) : null}
      </div>
      <div className="max-h-80 overflow-y-auto">
        <Table testId="email-notice-delivery-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('email_notices.recipients.column.email')}</Th>
              <Th>{t('email_notices.recipients.column.status')}</Th>
              <Th num>{t('email_notices.delivery.column.attempts')}</Th>
              <Th>{t('email_notices.delivery.column.error')}</Th>
              <Th>{t('email_notices.recipients.column.action')}</Th>
            </Tr>
          </THead>
          <TBody>
            {recipients.length === 0 ? (
              <TableEmpty colSpan={5}>{t('email_notices.delivery.empty')}</TableEmpty>
            ) : (
              recipients.map((row) => (
                <Tr key={row.account_id} interactive={false}>
                  <Td mono>{row.email}</Td>
                  <Td>
                    <Chip tone={recipientStatusTone(row.status)}>
                      {t(recipientStatusLabel(row.status))}
                    </Chip>
                  </Td>
                  <Td num mono>
                    {row.attempts}
                  </Td>
                  <Td className="max-w-xs truncate text-bad" title={row.last_error ?? undefined}>
                    {row.last_error ?? '—'}
                  </Td>
                  <Td>
                    {/*
                      RETRY IS FOR `failed` ROWS ONLY, and that is a safety
                      boundary rather than tidiness. A freshly frozen list is
                      entirely `pending`, so offering retry on those states put a
                      one-click `POST /send` next to every customer address
                      while the send button beside it was still correctly
                      greyed out — bypassing gate two, the confirm dialog, and
                      the type-the-count friction that exists precisely so a
                      bulk send cannot happen by reflex. A `failed` row can only
                      exist after a send that already cleared all of that, so
                      retrying one re-attempts an address the operator already
                      authorised.
                    */}
                    {row.status === 'sent' ? (
                      <span className="text-micro text-paper-low">
                        {row.sent_at === null ? '—' : row.sent_at.slice(0, 19).replace('T', ' ')}
                      </span>
                    ) : row.status === 'failed' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={retrying}
                        onClick={() => onRetry(row.account_id)}
                        data-testid={`email-notice-retry-${row.account_id}`}
                      >
                        {t('email_notices.delivery.retry')}
                      </Button>
                    ) : (
                      <span className="text-micro text-paper-low">—</span>
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      </div>
    </div>
  );
}
